/**
 * Breadcrumb Collector - CRM-Specific Session Tracking
 *
 * SIMPLIFIED VERSION: Sentry now handles automatic breadcrumb capture
 * (navigation, clicks, console, HTTP). This file now only provides:
 *
 * 1. Session statistics (CRM-specific metrics Sentry doesn't capture)
 * 2. Connection info (network quality for debugging)
 * 3. Manual breadcrumb helpers (form tracking, modals, slow operations)
 * 4. Visibility/resource error tracking (Sentry may miss these)
 *
 * @see instrumentation-client.ts - Sentry captures automatic breadcrumbs
 */

import type { Breadcrumb, BreadcrumbCategory, BreadcrumbLevel, SessionSummary, ConnectionInfo } from '../types';

// =============================================================================
// CONFIGURATION
// =============================================================================

const MAX_BREADCRUMBS = 50;
const MAX_DATA_STRING_LENGTH = 300;

// Fields to redact from breadcrumb data
const SENSITIVE_FIELDS = [
  'password', 'token', 'secret', 'api_key', 'apikey',
  'authorization', 'cookie', 'credit_card', 'creditcard', 'ssn', 'cvv', 'pin',
];

// =============================================================================
// SESSION TRACKING (CRM-Specific - Sentry doesn't provide this)
// =============================================================================

/** Session start time for duration tracking */
const sessionStartTime = Date.now();

/** Session statistics counters */
const sessionStats = {
  pagesVisited: new Set<string>(),
  pageHistory: [] as string[],
  totalClicks: 0,
  totalInputs: 0,
  httpRequests: 0,
  httpErrors: 0,
  consoleErrors: 0,
  peakMemoryMB: 0,
};

/** Get session duration in seconds */
export function getSessionDuration(): number {
  return Math.round((Date.now() - sessionStartTime) / 1000);
}

/** Track a page visit */
export function trackPageVisit(path: string): void {
  sessionStats.pagesVisited.add(path);
  sessionStats.pageHistory.push(path);
  if (sessionStats.pageHistory.length > 10) {
    sessionStats.pageHistory.shift();
  }
}

/** Increment session stat counters */
export function incrementSessionStat(stat: 'clicks' | 'inputs' | 'httpRequests' | 'httpErrors' | 'consoleErrors'): void {
  switch (stat) {
    case 'clicks': sessionStats.totalClicks++; break;
    case 'inputs': sessionStats.totalInputs++; break;
    case 'httpRequests': sessionStats.httpRequests++; break;
    case 'httpErrors': sessionStats.httpErrors++; break;
    case 'consoleErrors': sessionStats.consoleErrors++; break;
  }
}

/** Update peak memory if higher */
export function updatePeakMemory(memoryMB: number): void {
  if (memoryMB > sessionStats.peakMemoryMB) {
    sessionStats.peakMemoryMB = memoryMB;
  }
}

/** Get session summary for error reports (CRM-specific stats) */
export function getSessionSummary(): SessionSummary {
  return {
    sessionDurationSec: getSessionDuration(),
    pagesVisited: sessionStats.pagesVisited.size,
    pageHistory: [...sessionStats.pageHistory],
    totalClicks: sessionStats.totalClicks,
    totalInputs: sessionStats.totalInputs,
    httpRequests: sessionStats.httpRequests,
    httpErrors: sessionStats.httpErrors,
    consoleErrors: sessionStats.consoleErrors,
    peakMemoryMB: sessionStats.peakMemoryMB || undefined,
  };
}

/** Get connection/network information */
export function getConnectionInfo(): ConnectionInfo {
  if (typeof navigator === 'undefined') {
    return { onLine: true };
  }

  const connection = (navigator as Navigator & {
    connection?: {
      effectiveType?: string;
      downlink?: number;
      rtt?: number;
      saveData?: boolean;
    };
  }).connection;

  return {
    effectiveType: connection?.effectiveType,
    downlink: connection?.downlink,
    rtt: connection?.rtt,
    saveData: connection?.saveData,
    onLine: navigator.onLine,
  };
}

/** Get current viewport info */
export function getViewportInfo(): Record<string, unknown> {
  if (typeof window === 'undefined') return {};
  return {
    width: window.innerWidth,
    height: window.innerHeight,
    scrollX: Math.round(window.scrollX),
    scrollY: Math.round(window.scrollY),
    documentHeight: document.documentElement.scrollHeight,
    devicePixelRatio: window.devicePixelRatio,
  };
}

/** Get memory info if available */
export function getMemoryInfo(): Record<string, unknown> | undefined {
  if (typeof window === 'undefined') return undefined;
  const perf = performance as Performance & {
    memory?: { usedJSHeapSize: number; totalJSHeapSize: number; jsHeapSizeLimit: number };
  };
  if (perf.memory) {
    return {
      usedMB: Math.round(perf.memory.usedJSHeapSize / 1024 / 1024),
      totalMB: Math.round(perf.memory.totalJSHeapSize / 1024 / 1024),
      limitMB: Math.round(perf.memory.jsHeapSizeLimit / 1024 / 1024),
    };
  }
  return undefined;
}

// =============================================================================
// BREADCRUMB STORE (For CRM-specific manual breadcrumbs only)
// Note: Sentry captures automatic breadcrumbs separately
// =============================================================================

/** CRM-specific breadcrumbs (forms, modals, slow operations) */
let breadcrumbs: Breadcrumb[] = [];

/** Whether auto-capture is initialized */
let isInitialized = false;

// =============================================================================
// PUBLIC API
// =============================================================================

/**
 * Add a CRM-specific breadcrumb.
 * Note: Navigation, clicks, console, HTTP are captured by Sentry automatically.
 */
export function addBreadcrumb(
  category: BreadcrumbCategory,
  message: string,
  data?: Record<string, unknown>,
  level: BreadcrumbLevel = 'info'
): void {
  const breadcrumb: Breadcrumb = {
    timestamp: new Date().toISOString(),
    category,
    level,
    message,
    data: data ? sanitizeData({
      ...data,
      _sessionDurationSec: getSessionDuration(),
    }) : { _sessionDurationSec: getSessionDuration() },
  };

  breadcrumbs.push(breadcrumb);

  if (breadcrumbs.length > MAX_BREADCRUMBS) {
    breadcrumbs = breadcrumbs.slice(-MAX_BREADCRUMBS);
  }
}

/**
 * Get all CRM-specific breadcrumbs with context snapshot.
 */
export function getBreadcrumbs(): Breadcrumb[] {
  const contextBreadcrumb: Breadcrumb = {
    timestamp: new Date().toISOString(),
    category: 'state',
    level: 'info',
    message: 'Error context snapshot',
    data: {
      sessionDurationSec: getSessionDuration(),
      viewport: getViewportInfo(),
      memory: getMemoryInfo(),
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      visibilityState: typeof document !== 'undefined' ? document.visibilityState : undefined,
      onLine: typeof navigator !== 'undefined' ? navigator.onLine : undefined,
    },
  };

  return [...breadcrumbs, contextBreadcrumb];
}

/**
 * Clear all breadcrumbs.
 */
export function clearBreadcrumbs(): void {
  breadcrumbs = [];
}

/**
 * Initialize CRM-specific auto-capture.
 * Sentry handles: navigation, clicks, console, HTTP
 * We ENHANCE: detailed clicks, filter changes, visibility, resource errors
 */
export function initAutoBreadcrumbs(): () => void {
  if (typeof window === 'undefined') return () => {};
  if (isInitialized) return () => {};

  isInitialized = true;
  const cleanups: (() => void)[] = [];

  // 🆕 Enhanced click tracking - captures element details Sentry misses
  cleanups.push(initEnhancedClickTracking());

  // Visibility tracking - Sentry may not capture tab switches well
  cleanups.push(initVisibilityTracking());

  // Resource error tracking - image/script load failures
  cleanups.push(initResourceErrorTracking());

  return () => {
    cleanups.forEach((cleanup) => cleanup());
    isInitialized = false;
  };
}

// =============================================================================
// AUTO-CAPTURE: ENHANCED CLICK TRACKING (Element details Sentry misses)
// =============================================================================

/**
 * Get a minimal but useful CSS selector for an element.
 * Prioritizes: data-testid > aria-label > id > class + tag
 */
function getElementSelector(element: HTMLElement): string {
  // Priority 1: data-testid (best for debugging)
  if (element.dataset.testid) {
    return `[data-testid="${element.dataset.testid}"]`;
  }

  // Priority 2: aria-label (accessibility identifier)
  const ariaLabel = element.getAttribute('aria-label');
  if (ariaLabel) {
    return `[aria-label="${ariaLabel.slice(0, 50)}"]`;
  }

  // Priority 3: id (unique identifier)
  if (element.id) {
    return `#${element.id}`;
  }

  // Priority 4: name attribute (for form elements)
  const name = element.getAttribute('name');
  if (name) {
    return `${element.tagName.toLowerCase()}[name="${name}"]`;
  }

  // Priority 5: class + tag (fallback)
  const tag = element.tagName.toLowerCase();
  const classes = Array.from(element.classList).slice(0, 3).join('.');
  return classes ? `${tag}.${classes}` : tag;
}

/**
 * Get meaningful text content from an element (truncated, sanitized).
 */
function getElementText(element: HTMLElement): string | undefined {
  // Skip inputs - their content is tracked separately
  if (element.tagName === 'INPUT' || element.tagName === 'TEXTAREA') {
    return undefined;
  }

  // Get direct text content (not nested elements)
  const text = element.innerText?.trim().slice(0, 100);
  if (!text) return undefined;

  // Skip if it looks like a long list or code
  if (text.includes('\n') && text.split('\n').length > 3) {
    return text.split('\n')[0] + '...';
  }

  return text;
}

/**
 * Get the closest interactive parent (button, link, form element)
 */
function getInteractiveParent(element: HTMLElement): HTMLElement | null {
  const interactiveTags = ['BUTTON', 'A', 'INPUT', 'SELECT', 'TEXTAREA'];
  const interactiveRoles = ['button', 'link', 'menuitem', 'tab', 'checkbox', 'radio'];

  let current: HTMLElement | null = element;
  while (current) {
    if (interactiveTags.includes(current.tagName)) return current;
    const role = current.getAttribute('role');
    if (role && interactiveRoles.includes(role)) return current;
    current = current.parentElement;
  }
  return null;
}

function initEnhancedClickTracking(): () => void {
  let lastClickTime = 0;
  const DEBOUNCE_MS = 50; // Prevent duplicate captures

  const handleClick = (event: MouseEvent) => {
    // Debounce rapid clicks
    const now = Date.now();
    if (now - lastClickTime < DEBOUNCE_MS) return;
    lastClickTime = now;

    const target = event.target as HTMLElement;
    if (!target) return;

    // Guard against detached DOM nodes during RSC streaming/hydration
    // During streaming, event targets can reference nodes not yet fully in the tree
    try {
      // Find the meaningful interactive element (not just the text node clicked)
      const interactiveElement = getInteractiveParent(target) || target;

      // Build comprehensive click context
      const selector = getElementSelector(interactiveElement);
      const text = getElementText(interactiveElement);
      const tag = interactiveElement.tagName.toLowerCase();

      // Get position info for UI debugging
      const rect = interactiveElement.getBoundingClientRect();
      const clickPosition = {
        x: Math.round(event.clientX),
        y: Math.round(event.clientY),
        elementX: Math.round(rect.left),
        elementY: Math.round(rect.top),
        scrollY: Math.round(window.scrollY),
      };

      // Get data attributes (useful for debugging custom components)
      const dataAttrs: Record<string, string> = {};
      for (const [key, value] of Object.entries(interactiveElement.dataset)) {
        if (key !== 'testid' && value) {
          dataAttrs[key] = value.slice(0, 50);
        }
      }

      // Determine click type
      const href = interactiveElement.getAttribute('href');
      const type = interactiveElement.getAttribute('type');
      const role = interactiveElement.getAttribute('role');

      // Increment session stat
      incrementSessionStat('clicks');

      addBreadcrumb(
        'ui.click',
        `Click: ${text ? `"${text.slice(0, 40)}"` : selector}`,
        {
          selector,
          tag,
          text: text?.slice(0, 100),
          href: href ? sanitizeUrl(href) : undefined,
          type,
          role,
          position: clickPosition,
          dataAttrs: Object.keys(dataAttrs).length > 0 ? dataAttrs : undefined,
          // Context: what page area was clicked
          pageArea: getPageArea(interactiveElement),
        },
        'info'
      );
    } catch {
      // Silently ignore — DOM node may be detached during hydration/streaming
    }
  };

  window.addEventListener('click', handleClick, true);

  return () => {
    window.removeEventListener('click', handleClick, true);
  };
}

/**
 * Determine which area of the page was clicked (useful for debugging UX issues)
 */
function getPageArea(element: HTMLElement): string | undefined {
  // Look for common layout landmarks
  const landmarks = [
    { selector: '[role="navigation"]', area: 'navigation' },
    { selector: 'nav', area: 'navigation' },
    { selector: 'header', area: 'header' },
    { selector: 'aside', area: 'sidebar' },
    { selector: '[role="main"]', area: 'main-content' },
    { selector: 'main', area: 'main-content' },
    { selector: 'footer', area: 'footer' },
    { selector: '[role="dialog"]', area: 'modal' },
    { selector: '[data-radix-dialog-content]', area: 'modal' },
    { selector: '[data-radix-popover-content]', area: 'popover' },
    { selector: '[data-radix-dropdown-menu-content]', area: 'dropdown' },
    { selector: '.filter', area: 'filters' },
    { selector: '[class*="filter"]', area: 'filters' },
    { selector: 'form', area: 'form' },
    { selector: 'table', area: 'table' },
    { selector: '[class*="kanban"]', area: 'kanban' },
  ];

  for (const { selector, area } of landmarks) {
    if (element.closest(selector)) {
      return area;
    }
  }

  return undefined;
}

// =============================================================================
// AUTO-CAPTURE: VISIBILITY (Tab switches, online/offline)
// =============================================================================

function initVisibilityTracking(): () => void {
  const handleVisibilityChange = () => {
    const state = document.visibilityState;
    addBreadcrumb(
      'ui.visibility',
      `Tab ${state === 'visible' ? 'became visible' : 'hidden'}`,
      { visibilityState: state, hidden: document.hidden },
      state === 'hidden' ? 'warning' : 'info'
    );
  };

  const handleOnline = () => {
    addBreadcrumb('state', 'Network: back online', { onLine: true }, 'info');
  };

  const handleOffline = () => {
    addBreadcrumb('state', 'Network: went offline', { onLine: false }, 'warning');
  };

  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('online', handleOnline);
  window.addEventListener('offline', handleOffline);

  return () => {
    document.removeEventListener('visibilitychange', handleVisibilityChange);
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
  };
}

// =============================================================================
// AUTO-CAPTURE: RESOURCE ERRORS (Image/script failures)
// =============================================================================

function initResourceErrorTracking(): () => void {
  const handleError = (event: Event) => {
    const target = event.target as HTMLElement;
    if (!target) return;

    if (!['IMG', 'SCRIPT', 'LINK'].includes(target.tagName)) return;

    const src =
      (target as HTMLImageElement).src ||
      (target as HTMLScriptElement).src ||
      (target as HTMLLinkElement).href;

    addBreadcrumb(
      'performance',
      `Resource failed: ${target.tagName.toLowerCase()}`,
      { tag: target.tagName.toLowerCase(), src: src ? sanitizeUrl(src) : undefined },
      'error'
    );
  };

  window.addEventListener('error', handleError, true);

  return () => {
    window.removeEventListener('error', handleError, true);
  };
}

// =============================================================================
// DATA SANITIZATION
// =============================================================================

/**
 * Sanitize data object to remove PII and sensitive information.
 */
function sanitizeData(data: Record<string, unknown>): Record<string, unknown> {
  const sanitized: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(data)) {
    const lowerKey = key.toLowerCase();
    if (SENSITIVE_FIELDS.some((field) => lowerKey.includes(field))) {
      sanitized[key] = '[REDACTED]';
      continue;
    }

    if (typeof value === 'string') {
      if (value.includes('@') && value.includes('.') && !value.startsWith('http')) {
        sanitized[key] = '[EMAIL]';
        continue;
      }
      if (/^\+?[\d\s()-]{10,}$/.test(value)) {
        sanitized[key] = '[PHONE]';
        continue;
      }
      sanitized[key] = value.slice(0, MAX_DATA_STRING_LENGTH);
      continue;
    }

    if (typeof value === 'number' || typeof value === 'boolean' || value === null) {
      sanitized[key] = value;
      continue;
    }

    if (value === undefined) continue;

    if (typeof value === 'object' && !Array.isArray(value)) {
      sanitized[key] = sanitizeData(value as Record<string, unknown>);
      continue;
    }

    if (Array.isArray(value)) {
      if (value.length <= 5 && value.every((v) => typeof v === 'string')) {
        sanitized[key] = value.map((v) => String(v).slice(0, 50));
      } else {
        sanitized[key] = `[Array(${value.length})]`;
      }
      continue;
    }
  }

  return sanitized;
}

/**
 * Sanitize URLs to remove sensitive query parameters.
 */
function sanitizeUrl(url: string): string {
  try {
    const parsed = new URL(url, window.location.origin);
    const sensitiveParams = ['token', 'key', 'secret', 'password', 'auth', 'session', 'code'];
    sensitiveParams.forEach((param) => {
      if (parsed.searchParams.has(param)) {
        parsed.searchParams.set(param, '[REDACTED]');
      }
    });
    const query = parsed.search ? parsed.search.slice(0, 100) : '';
    return parsed.pathname + query;
  } catch (_) {
    return url.split('?')[0].slice(0, 100);
  }
}

// =============================================================================
// FORM & STATE TRACKING HELPERS (Manual - Use in Components)
// =============================================================================

/**
 * Track a form submission with field summary.
 */
export function trackFormSubmission(formName: string, data?: Record<string, unknown>): void {
  const sanitizedData = data ? sanitizeData(data) : undefined;
  const fieldCount = data ? Object.keys(data).length : 0;

  addBreadcrumb('form', `Form submitted: ${formName}`, {
    formName,
    fieldCount,
    fields: data ? Object.keys(data).slice(0, 20) : undefined,
    ...sanitizedData,
  });
}

/**
 * Track a form field change.
 */
export function trackFormFieldChange(formName: string, fieldName: string): void {
  addBreadcrumb('form', `Field changed: ${formName}.${fieldName}`, {
    form: formName,
    field: fieldName,
  });
}

/**
 * Track a modal/dialog open.
 */
export function trackModalOpen(modalName: string, data?: Record<string, unknown>): void {
  addBreadcrumb('state', `Modal opened: ${modalName}`, { modal: modalName, ...data });
}

/**
 * Track a modal/dialog close.
 */
export function trackModalClose(modalName: string, reason?: string): void {
  addBreadcrumb('state', `Modal closed: ${modalName}`, { modal: modalName, reason });
}

/**
 * Track a slow operation for performance debugging.
 */
export function trackSlowOperation(operationName: string, durationMs: number, data?: Record<string, unknown>): void {
  const level: BreadcrumbLevel = durationMs > 3000 ? 'error' : durationMs > 1000 ? 'warning' : 'info';
  addBreadcrumb('performance', `Slow operation: ${operationName} (${durationMs}ms)`, {
    operation: operationName,
    durationMs,
    ...data,
  }, level);
}

// =============================================================================
// FILTER TRACKING HELPERS (For debugging filter-related UX issues)
// =============================================================================

/**
 * Track a filter being applied.
 * Use this in filter components to capture what filters users are applying.
 */
export function trackFilterApply(filterName: string, value: string | string[], page: string): void {
  addBreadcrumb('state', `Filter applied: ${filterName}`, {
    filter: filterName,
    value: Array.isArray(value) ? value.slice(0, 10) : value?.slice(0, 100),
    page,
    action: 'apply',
  });
}

/**
 * Track a filter being cleared.
 */
export function trackFilterClear(filterName: string | 'all', page: string): void {
  addBreadcrumb('state', `Filter cleared: ${filterName}`, {
    filter: filterName,
    page,
    action: 'clear',
  });
}

/**
 * Track filter search/query input.
 */
export function trackFilterSearch(query: string, page: string, resultsCount?: number): void {
  addBreadcrumb('state', `Filter search: "${query.slice(0, 50)}"`, {
    query: query.slice(0, 100),
    page,
    resultsCount,
    action: 'search',
  });
}

/**
 * Track URL-based navigation with filters.
 * Useful for debugging when filters are stored in URL params.
 */
export function trackFilterNavigation(page: string, params: Record<string, string | undefined>): void {
  // Filter out undefined/empty params
  const activeParams: Record<string, string> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value) {
      activeParams[key] = value.slice(0, 50);
    }
  }

  addBreadcrumb('navigation', `Filter navigation: ${page}`, {
    page,
    params: activeParams,
    paramCount: Object.keys(activeParams).length,
  });
}

/**
 * Track when user encounters a filter-related issue.
 * Call this when the filter UI enters an unexpected state.
 */
export function trackFilterIssue(issue: string, context: Record<string, unknown>): void {
  addBreadcrumb('state', `Filter issue: ${issue}`, {
    issue,
    ...sanitizeData(context),
  }, 'warning');
}
